import type { Translate } from 'next-translate';

import type { SocialProfileType } from '@/types/socialProfileType';

import { POST, STORY } from '@/lib/posts/postTypes';
import {
    FACEBOOK,
    INSTAGRAM,
    LINKEDIN,
    PINTEREST,
    TIKTOK_BUSINESS,
    TWITTER,
    X,
    YOUTUBE
} from '@/lib/socialProfile';

export const FB_VIDEOS_PAGE = 'FB_VIDEOS_PAGE';
export const FB_REELS_PAGE = 'FB_REELS_PAGE';

export const GROUP = 'group';

export type SortOptionType = {
    label: string;
    label_selected: string;
    value: string;
    order: 'asc' | 'desc';
    notAvailableWithNetworks?: SocialProfileType[];
};

export type SortOptionPageType = SortOptionType & { filter_post_type: string[] };

const FacebookSortOptions = (t: Translate) => {
    return {
        created_desc: {
            label: t('sort.by_most_recent'),
            label_selected: t('sort.posts.facebook.most_recent_selected'),
            value: 'created',
            order: 'desc'
        },
        created_asc: {
            label: t('sort.by_least_recent'),
            label_selected: t('sort.posts.facebook.least_recent_selected'),
            value: 'created',
            order: 'asc'
        },
        reactions_desc: {
            label: t('sort.by_most_reactions'),
            label_selected: t('sort.posts.facebook.most_reactions_selected'),
            value: 'reactions',
            order: 'desc'
        },
        reactions_asc: {
            label: t('sort.by_least_reactions'),
            label_selected: t('sort.posts.facebook.least_reactions_selected'),
            value: 'reactions',
            order: 'asc'
        },
        comments_desc: {
            label: t('sort.by_most_commented'),
            label_selected: t('sort.posts.facebook.most_commented_selected'),
            value: 'comments',
            order: 'desc'
        },
        comments_asc: {
            label: t('sort.by_least_commented'),
            label_selected: t('sort.posts.facebook.least_commented_selected'),
            value: 'comments',
            order: 'asc'
        },
        shares_desc: {
            label: t('sort.by_most_shares'),
            label_selected: t('sort.posts.facebook.most_shares_selected'),
            value: 'shares',
            order: 'desc'
        },
        shares_asc: {
            label: t('sort.by_least_shares'),
            label_selected: t('sort.posts.facebook.least_shares_selected'),
            value: 'shares',
            order: 'asc'
        },
        clicks_desc: {
            label: t('sort.by_most_clicks'),
            label_selected: t('sort.posts.facebook.most_clicks_selected'),
            value: 'clicks',
            order: 'desc'
        },
        clicks_asc: {
            label: t('sort.by_least_clicks'),
            label_selected: t('sort.posts.facebook.least_clicks_selected'),
            value: 'clicks',
            order: 'asc'
        },
        engagements_desc: {
            label: t('sort.by_most_engaging'),
            label_selected: t('sort.posts.facebook.most_engaging_selected'),
            value: 'engagements',
            order: 'desc'
        },
        engagements_asc: {
            label: t('sort.by_least_engaging'),
            label_selected: t('sort.posts.facebook.least_engaging_selected'),
            value: 'engagements',
            order: 'asc'
        },
        video_views_desc: {
            label: t('sort.by_most_views'),
            label_selected: t('sort.posts.facebook.most_views_selected'),
            value: 'video_views',
            order: 'desc'
        },
        video_views_asc: {
            label: t('sort.by_least_views'),
            label_selected: t('sort.posts.facebook.least_views_selected'),
            value: 'video_views',
            order: 'asc'
        },
        reach_desc: {
            label: t('sort.by_most_reach'),
            label_selected: t('sort.posts.facebook.most_reach_selected'),
            value: 'reach',
            order: 'desc'
        },
        reach_asc: {
            label: t('sort.by_least_reach'),
            label_selected: t('sort.posts.facebook.least_reach_selected'),
            value: 'reach',
            order: 'asc'
        },
        impressions_desc: {
            label: t('sort.by_most_impressions'),
            label_selected: t('sort.posts.facebook.most_impressions_selected'),
            value: 'impressions',
            order: 'desc'
        },
        impressions_asc: {
            label: t('sort.by_least_impressions'),
            label_selected: t('sort.posts.facebook.least_impressions_selected'),
            value: 'impressions',
            order: 'asc'
        },
        engagement_on_reach_desc: {
            label: t('sort.by_most_engagement_on_reach'),
            label_selected: t('sort.posts.facebook.most_engagement_on_reach_selected'),
            value: 'engagement_on_reach',
            order: 'desc'
        },
        engagement_on_reach_asc: {
            label: t('sort.by_least_engagement_on_reach'),
            label_selected: t('sort.posts.facebook.least_engagement_on_reach_selected'),
            value: 'engagement_on_reach',
            order: 'asc'
        },
        like_reactions_desc: {
            label: t('sort.by_most_like_reactions'),
            label_selected: t('sort.posts.facebook.most_like_reactions_selected'),
            value: 'like_reactions',
            order: 'desc'
        },
        like_reactions_asc: {
            label: t('sort.by_least_like_reactions'),
            label_selected: t('sort.posts.facebook.least_like_reactions_selected'),
            value: 'like_reactions',
            order: 'asc'
        },
        love_reactions_desc: {
            label: t('sort.by_most_love_reactions'),
            label_selected: t('sort.posts.facebook.most_love_reactions_selected'),
            value: 'love_reactions',
            order: 'desc'
        },
        love_reactions_asc: {
            label: t('sort.by_least_love_reactions'),
            label_selected: t('sort.posts.facebook.least_love_reactions_selected'),
            value: 'love_reactions',
            order: 'asc'
        },
        wow_reactions_desc: {
            label: t('sort.by_most_wow_reactions'),
            label_selected: t('sort.posts.facebook.most_wow_reactions_selected'),
            value: 'wow_reactions',
            order: 'desc'
        },
        wow_reactions_asc: {
            label: t('sort.by_least_wow_reactions'),
            label_selected: t('sort.posts.facebook.least_wow_reactions_selected'),
            value: 'wow_reactions',
            order: 'asc'
        },
        haha_reactions_desc: {
            label: t('sort.by_most_haha_reactions'),
            label_selected: t('sort.posts.facebook.most_haha_reactions_selected'),
            value: 'haha_reactions',
            order: 'desc'
        },
        haha_reactions_asc: {
            label: t('sort.by_least_haha_reactions'),
            label_selected: t('sort.posts.facebook.least_haha_reactions_selected'),
            value: 'haha_reactions',
            order: 'asc'
        },
        sad_reactions_desc: {
            label: t('sort.by_most_sad_reactions'),
            label_selected: t('sort.posts.facebook.most_sad_reactions_selected'),
            value: 'sad_reactions',
            order: 'desc'
        },
        sad_reactions_asc: {
            label: t('sort.by_least_sad_reactions'),
            label_selected: t('sort.posts.facebook.least_sad_reactions_selected'),
            value: 'sad_reactions',
            order: 'asc'
        },
        angry_reactions_desc: {
            label: t('sort.by_most_angry_reactions'),
            label_selected: t('sort.posts.facebook.most_angry_reactions_selected'),
            value: 'angry_reactions',
            order: 'desc'
        },
        angry_reactions_asc: {
            label: t('sort.by_least_angry_reactions'),
            label_selected: t('sort.posts.facebook.least_angry_reactions_selected'),
            value: 'angry_reactions',
            order: 'asc'
        },
        video_views_10s_desc: {
            label: t('sort.by_most_views_10_sec'),
            label_selected: t('sort.posts.facebook.by_most_views_10_sec'),
            value: 'video_views_10s',
            order: 'desc',
            filter_post_type: ['video']
        },
        video_views_complete_desc: {
            label: t('sort.by_most_views_complete'),
            label_selected: t('sort.posts.facebook.by_most_views_complete'),
            value: 'video_views_complete',
            order: 'desc',
            filter_post_type: ['video']
        },
        plays_desc: {
            label: t('sort.by_most_plays'),
            label_selected: t('sort.posts.instagram.most_plays_selected'),
            value: 'plays',
            order: 'desc'
        },
        plays_asc: {
            label: t('sort.by_least_plays'),
            label_selected: t('sort.posts.instagram.least_plays_selected'),
            value: 'plays',
            order: 'asc'
        }
    };
};

export const postSortOptions = (t: Translate, page?: string, noSortTypesSelect?: boolean) => {
    const fbSortOptions = FacebookSortOptions(t);

    switch (page) {
        case FB_VIDEOS_PAGE:
            return {
                [FACEBOOK]: [
                    fbSortOptions.video_views_desc,
                    fbSortOptions.video_views_10s_desc,
                    fbSortOptions.video_views_complete_desc
                ] as SortOptionPageType[]
            };
        case FB_REELS_PAGE:
            return {
                [FACEBOOK]: [
                    fbSortOptions.plays_desc,
                    fbSortOptions.plays_asc,
                    fbSortOptions.created_desc,
                    fbSortOptions.created_asc,
                    fbSortOptions.comments_desc,
                    fbSortOptions.comments_asc,
                    fbSortOptions.shares_desc,
                    fbSortOptions.shares_asc,
                    fbSortOptions.reach_desc,
                    fbSortOptions.reach_asc,
                    fbSortOptions.reactions_desc,
                    fbSortOptions.reactions_asc
                ].map((sort) => ({ ...sort, filter_post_type: ['reel'] })) as SortOptionPageType[]
            };
        default:
            return {
                [INSTAGRAM]: [
                    {
                        label: t('sort.by_most_recent'),
                        label_selected: t('sort.posts.instagram.most_recent_selected'),
                        value: 'created',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_recent'),
                        label_selected: t('sort.posts.instagram.least_recent_selected'),
                        value: 'created',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_liked'),
                        label_selected: t('sort.posts.instagram.most_liked_selected'),
                        value: 'likes',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_liked'),
                        label_selected: t('sort.posts.instagram.least_liked_selected'),
                        value: 'likes',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_commented'),
                        label_selected: t('sort.posts.instagram.most_commented_selected'),
                        value: 'comments',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_commented'),
                        label_selected: t('sort.posts.instagram.least_commented_selected'),
                        value: 'comments',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_saves'),
                        label_selected: t('sort.posts.instagram.most_saves_selected'),
                        value: 'saves',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_saves'),
                        label_selected: t('sort.posts.instagram.least_saves_selected'),
                        value: 'saves',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_reach'),
                        label_selected: t('sort.posts.instagram.most_reach_selected'),
                        value: 'reach',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_reach'),
                        label_selected: t('sort.posts.instagram.least_reach_selected'),
                        value: 'reach',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_views'),
                        label_selected: noSortTypesSelect
                            ? t('sort.posts.instagram.most_views_selected')
                            : t('sort.posts.instagram.most_viewed'),
                        value: 'impressions_views',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_views'),
                        label_selected: noSortTypesSelect
                            ? t('sort.posts.instagram.least_views_selected')
                            : t('sort.posts.instagram.least_viewed'),
                        value: 'impressions_views',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_engaging'),
                        label_selected: t('sort.posts.instagram.most_engaging_selected'),
                        value: 'engagements',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_engaging'),
                        label_selected: t('sort.posts.instagram.least_engaging_selected'),
                        value: 'engagements',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_engagement_on_reach'),
                        label_selected: t('sort.posts.instagram.most_engagement_on_reach_selected'),
                        value: 'engagement_on_reach',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_engagement_on_reach'),
                        label_selected: t(
                            'sort.posts.instagram.least_engagement_on_reach_selected'
                        ),
                        value: 'engagement_on_reach',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_shares'),
                        label_selected: t('sort.posts.instagram.most_shares_selected'),
                        value: 'shares',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_shares'),
                        label_selected: t('sort.posts.instagram.least_shares_selected'),
                        value: 'shares',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_plays'),
                        label_selected: t('sort.posts.instagram.most_plays_selected'),
                        value: 'plays',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_plays'),
                        label_selected: t('sort.posts.instagram.least_plays_selected'),
                        value: 'plays',
                        order: 'asc'
                    }
                ] as SortOptionType[],
                [FACEBOOK]: Object.values(fbSortOptions) as SortOptionType[],
                [TWITTER]: [
                    {
                        label: t('sort.by_most_recent'),
                        label_selected: t('sort.posts.twitter.most_recent_selected'),
                        value: 'created',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_recent'),
                        label_selected: t('sort.posts.twitter.least_recent_selected'),
                        value: 'created',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_liked'),
                        label_selected: t('sort.posts.twitter.most_liked_selected'),
                        value: 'likes',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_liked'),
                        label_selected: t('sort.posts.twitter.least_liked_selected'),
                        value: 'likes',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_least_replied'),
                        label_selected: t('sort.posts.twitter.least_replied_selected'),
                        value: 'replies',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_replied'),
                        label_selected: t('sort.posts.twitter.most_replied_selected'),
                        value: 'replies',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_retweeted'),
                        label_selected: t('sort.posts.twitter.least_retweeted_selected'),
                        value: 'retweets',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_retweeted'),
                        label_selected: t('sort.posts.twitter.most_retweeted_selected'),
                        value: 'retweets',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_most_impressions'),
                        label_selected: t('sort.posts.twitter.most_impressions_selected'),
                        value: 'impressions',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_impressions'),
                        label_selected: t('sort.posts.twitter.least_impressions_selected'),
                        value: 'impressions',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_engaging'),
                        label_selected: t('sort.posts.twitter.most_engaging_selected'),
                        value: 'engagements',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_engaging'),
                        label_selected: t('sort.posts.twitter.least_engaging_selected'),
                        value: 'engagements',
                        order: 'asc'
                    }
                ] as SortOptionType[],
                [X]: [
                    {
                        label: t('sort.by_most_recent'),
                        label_selected: t('sort.posts.twitter.most_recent_selected'),
                        value: 'created',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_recent'),
                        label_selected: t('sort.posts.twitter.least_recent_selected'),
                        value: 'created',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_liked'),
                        label_selected: t('sort.posts.twitter.most_liked_selected'),
                        value: 'likes',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_liked'),
                        label_selected: t('sort.posts.twitter.least_liked_selected'),
                        value: 'likes',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_least_replied'),
                        label_selected: t('sort.posts.twitter.least_replied_selected'),
                        value: 'replies',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_replied'),
                        label_selected: t('sort.posts.twitter.most_replied_selected'),
                        value: 'replies',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_retweeted'),
                        label_selected: t('sort.posts.twitter.least_retweeted_selected'),
                        value: 'retweets',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_retweeted'),
                        label_selected: t('sort.posts.twitter.most_retweeted_selected'),
                        value: 'retweets',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_most_impressions'),
                        label_selected: t('sort.posts.twitter.most_impressions_selected'),
                        value: 'impressions',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_impressions'),
                        label_selected: t('sort.posts.twitter.least_impressions_selected'),
                        value: 'impressions',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_engaging'),
                        label_selected: t('sort.posts.twitter.most_engaging_selected'),
                        value: 'engagements',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_engaging'),
                        label_selected: t('sort.posts.twitter.least_engaging_selected'),
                        value: 'engagements',
                        order: 'asc'
                    }
                ] as SortOptionType[],
                [LINKEDIN]: [
                    {
                        label: t('sort.by_most_recent'),
                        label_selected: t('sort.posts.linkedin.most_recent_selected'),
                        value: 'created',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_recent'),
                        label_selected: t('sort.posts.linkedin.least_recent_selected'),
                        value: 'created',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_liked'),
                        label_selected: t('sort.posts.linkedin.most_liked_selected'),
                        value: 'likes',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_liked'),
                        label_selected: t('sort.posts.linkedin.least_liked_selected'),
                        value: 'likes',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_commented'),
                        label_selected: t('sort.posts.linkedin.most_commented_selected'),
                        value: 'comments',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_commented'),
                        label_selected: t('sort.posts.linkedin.least_commented_selected'),
                        value: 'comments',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_shares'),
                        label_selected: t('sort.posts.linkedin.most_shares_selected'),
                        value: 'shares',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_shares'),
                        label_selected: t('sort.posts.linkedin.least_shares_selected'),
                        value: 'shares',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_clicks'),
                        label_selected: t('sort.posts.linkedin.most_clicks_selected'),
                        value: 'clicks',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_clicks'),
                        label_selected: t('sort.posts.linkedin.least_clicks_selected'),
                        value: 'clicks',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_impressions'),
                        label_selected: t('sort.posts.linkedin.most_impressions_selected'),
                        value: 'impressions',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_impressions'),
                        label_selected: t('sort.posts.linkedin.least_impressions_selected'),
                        value: 'impressions',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_engaging'),
                        label_selected: t('sort.posts.linkedin.most_engaging_selected'),
                        value: 'engagements',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_engaging'),
                        label_selected: t('sort.posts.linkedin.least_engaging_selected'),
                        value: 'engagements',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_reach'),
                        label_selected: t('sort.posts.linkedin.most_reach_selected'),
                        value: 'reach',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_reach'),
                        label_selected: t('sort.posts.linkedin.least_reach_selected'),
                        value: 'reach',
                        order: 'asc'
                    }
                ] as SortOptionType[],
                [TIKTOK_BUSINESS]: [
                    {
                        label: t('sort.by_most_recent'),
                        label_selected: t('sort.posts.tiktok.most_recent_selected'),
                        value: 'created',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_recent'),
                        label_selected: t('sort.posts.tiktok.least_recent_selected'),
                        value: 'created',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_engaging'),
                        label_selected: t('sort.posts.tiktok.most_engaging_selected'),
                        value: 'engagements',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_engaging'),
                        label_selected: t('sort.posts.tiktok.least_engaging_selected'),
                        value: 'engagements',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_views'),
                        label_selected: t('sort.posts.tiktok.most_views_selected'),
                        value: 'video_views',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_views'),
                        label_selected: t('sort.posts.tiktok.least_views_selected'),
                        value: 'video_views',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_engagement_on_views'),
                        label_selected: t('sort.posts.tiktok.most_engagement_on_views_selected'),
                        value: 'engagement_on_views',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_engagement_on_views'),
                        label_selected: t('sort.posts.tiktok.least_engagement_on_views_selected'),
                        value: 'engagement_on_views',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_liked'),
                        label_selected: t('sort.posts.tiktok.most_liked_selected'),
                        value: 'likes',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_liked'),
                        label_selected: t('sort.posts.tiktok.least_liked_selected'),
                        value: 'likes',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_commented'),
                        label_selected: t('sort.posts.tiktok.most_commented_selected'),
                        value: 'comments',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_commented'),
                        label_selected: t('sort.posts.tiktok.least_commented_selected'),
                        value: 'comments',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_shares'),
                        label_selected: t('sort.posts.tiktok.most_shares_selected'),
                        value: 'shares',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_shares'),
                        label_selected: t('sort.posts.tiktok.least_shares_selected'),
                        value: 'shares',
                        order: 'asc'
                    }
                ] as SortOptionType[],
                [PINTEREST]: [
                    {
                        label: t('sort.by_most_recent'),
                        label_selected: t('sort.posts.pinterest.most_recent_selected'),
                        value: 'created',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_recent'),
                        label_selected: t('sort.posts.pinterest.least_recent_selected'),
                        value: 'created',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_engaging'),
                        label_selected: t('sort.posts.pinterest.most_engaging_selected'),
                        value: 'engagements',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_engaging'),
                        abel_selected: t('sort.posts.pinterest.least_engaging_selected'),
                        value: 'engagements',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_impressions'),
                        label_selected: t('sort.posts.pinterest.most_impressions_selected'),
                        value: 'impressions',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_impressions'),
                        abel_selected: t('sort.posts.pinterest.least_impressions_selected'),
                        value: 'impressions',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_engagement_on_impression'),
                        label_selected: t(
                            'sort.posts.pinterest.most_engagement_on_impression_selected'
                        ),
                        value: 'engagement_on_impression',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_engagement_on_impression'),
                        abel_selected: t(
                            'sort.posts.pinterest.least_engagement_on_impression_selected'
                        ),
                        value: 'engagement_on_impression',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_reactions'),
                        label_selected: t('sort.posts.pinterest.most_reactions_selected'),
                        value: 'reactions',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_reactions'),
                        label_selected: t('sort.posts.pinterest.least_reactions_selected'),
                        value: 'reactions',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_commented'),
                        label_selected: t('sort.posts.pinterest.most_commented_selected'),
                        value: 'comments',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_commented'),
                        label_selected: t('sort.posts.pinterest.least_commented_selected'),
                        value: 'comments',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_saves'),
                        label_selected: t('sort.posts.pinterest.most_saves_selected'),
                        value: 'saves',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_saves'),
                        label_selected: t('sort.posts.pinterest.least_saves_selected'),
                        value: 'saves',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_views'),
                        label_selected: t('sort.posts.pinterest.most_views_selected'),
                        value: 'video_views',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_views'),
                        label_selected: t('sort.posts.pinterest.least_views_selected'),
                        value: 'video_views',
                        order: 'asc'
                    }
                ] as SortOptionType[],
                [YOUTUBE]: [
                    {
                        label: t('sort.by_most_recent'),
                        label_selected: t('sort.posts.youtube.most_recent_selected'),
                        value: 'created',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_recent'),
                        label_selected: t('sort.posts.youtube.least_recent_selected'),
                        value: 'created',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_views'),
                        label_selected: t('sort.posts.youtube.most_views_selected'),
                        value: 'views',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_views'),
                        label_selected: t('sort.posts.youtube.least_views_selected'),
                        value: 'views',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_liked'),
                        label_selected: t('sort.posts.youtube.most_liked_selected'),
                        value: 'likes',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_liked'),
                        label_selected: t('sort.posts.youtube.least_liked_selected'),
                        value: 'likes',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_disliked'),
                        label_selected: t('sort.posts.youtube.most_disliked_selected'),
                        value: 'dislikes',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_disliked'),
                        label_selected: t('sort.posts.youtube.least_disliked_selected'),
                        value: 'dislikes',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_commented'),
                        label_selected: t('sort.posts.youtube.most_commented_selected'),
                        value: 'comments',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_commented'),
                        label_selected: t('sort.posts.youtube.least_commented_selected'),
                        value: 'comments',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_shares'),
                        label_selected: t('sort.posts.youtube.most_shares_selected'),
                        value: 'shares',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_shares'),
                        label_selected: t('sort.posts.youtube.least_shares_selected'),
                        value: 'shares',
                        order: 'asc'
                    }
                ] as SortOptionType[],
                [GROUP]: [
                    {
                        label: t('sort.by_most_recent'),
                        label_selected: t('sort.posts.group.most_recent_selected'),
                        value: 'created',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_recent'),
                        label_selected: t('sort.posts.group.least_recent_selected'),
                        value: 'created',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_engaging'),
                        label_selected: t('sort.posts.group.most_engaging_selected'),
                        value: 'engagements',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_engaging'),
                        label_selected: t('sort.posts.group.least_engaging_selected'),
                        value: 'engagements',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_engagement_on_reach'),
                        label_selected: t('sort.posts.group.most_engagement_on_reach_selected'),
                        value: 'engagement_on_reach',
                        order: 'desc',
                        notAvailableWithNetworks: [
                            LINKEDIN,
                            TWITTER,
                            TIKTOK_BUSINESS,
                            YOUTUBE,
                            PINTEREST
                        ]
                    },
                    {
                        label: t('sort.by_least_engagement_on_reach'),
                        label_selected: t('sort.posts.group.least_engagement_on_reach_selected'),
                        value: 'engagement_on_reach',
                        order: 'asc',
                        notAvailableWithNetworks: [
                            LINKEDIN,
                            TWITTER,
                            TIKTOK_BUSINESS,
                            YOUTUBE,
                            PINTEREST
                        ]
                    },
                    {
                        label: t('sort.by_most_liked'),
                        label_selected: t('sort.posts.group.most_liked_selected'),
                        value: 'likes',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_liked'),
                        label_selected: t('sort.posts.group.least_liked_selected'),
                        value: 'likes',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_commented'),
                        label_selected: t('sort.posts.group.most_commented_selected'),
                        value: 'comments',
                        order: 'desc'
                    },
                    {
                        label: t('sort.by_least_commented'),
                        label_selected: t('sort.posts.group.least_commented_selected'),
                        value: 'comments',
                        order: 'asc'
                    },
                    {
                        label: t('sort.by_most_reach'),
                        label_selected: t('sort.posts.group.most_reach_selected'),
                        value: 'reach',
                        order: 'desc',
                        notAvailableWithNetworks: [LINKEDIN, TWITTER, TIKTOK_BUSINESS, YOUTUBE]
                    },
                    {
                        label: t('sort.by_least_reach'),
                        label_selected: t('sort.posts.group.least_reach_selected'),
                        value: 'reach',
                        order: 'asc',
                        notAvailableWithNetworks: [LINKEDIN, TWITTER, TIKTOK_BUSINESS, YOUTUBE]
                    },
                    {
                        label: t('sort.by_most_views_impressions'),
                        label_selected: t('sort.posts.group.most_views_impressions_selected'),
                        value: 'video_views',
                        order: 'desc',
                        notAvailableWithNetworks: [TWITTER]
                    },
                    {
                        label: t('sort.by_least_views_impressions'),
                        label_selected: t('sort.posts.group.least_views_impressions_selected'),
                        value: 'video_views',
                        order: 'asc',
                        notAvailableWithNetworks: [TWITTER]
                    }
                ]
            };
    }
};

export const storiesSortOptions = (t: Translate) => {
    return {
        [INSTAGRAM]: [
            {
                label: t('sort.by_most_recent'),
                label_selected: t('sort.stories.most_recent_selected'),
                value: 'created',
                order: 'desc'
            },
            {
                label: t('sort.by_least_recent'),
                label_selected: t('sort.stories.least_recent_selected'),
                value: 'created',
                order: 'asc'
            },
            {
                label: t('sort.by_most_views'),
                label_selected: t('sort.stories.most_views_selected'),
                value: 'views',
                order: 'desc'
            },
            {
                label: t('sort.by_least_views'),
                label_selected: t('sort.stories.least_views_selected'),
                value: 'views',
                order: 'asc'
            },
            {
                label: t('sort.by_most_reach'),
                label_selected: t('sort.stories.most_reach_selected'),
                value: 'reach',
                order: 'desc'
            },
            {
                label: t('sort.by_least_reach'),
                label_selected: t('sort.stories.least_reach_selected'),
                value: 'reach',
                order: 'asc'
            },
            {
                label: t('sort.by_most_reach_rate'),
                label_selected: t('sort.stories.most_reach_rate_selected'),
                value: 'reach_rate',
                order: 'desc'
            },
            {
                label: t('sort.by_least_reach_rate'),
                label_selected: t('sort.stories.least_reach_rate_selected'),
                value: 'reach_rate',
                order: 'asc'
            },
            {
                label: t('sort.by_most_completed'),
                label_selected: t('sort.stories.most_completed_selected'),
                value: 'completed',
                order: 'desc'
            },
            {
                label: t('sort.by_least_completed'),
                label_selected: t('sort.stories.least_completed_selected'),
                value: 'completed',
                order: 'asc'
            },
            {
                label: t('sort.by_most_completion_rate'),
                label_selected: t('sort.stories.most_completion_rate_selected'),
                value: 'completed_rate',
                order: 'desc'
            },
            {
                label: t('sort.by_least_completion_rate'),
                label_selected: t('sort.stories.least_completion_rate_selected'),
                value: 'completed_rate',
                order: 'asc'
            },
            {
                label: t('sort.by_most_replied'),
                label_selected: t('sort.stories.most_replied_selected'),
                value: 'replies',
                order: 'desc'
            },
            {
                label: t('sort.by_least_replied'),
                label_selected: t('sort.stories.least_replied_selected'),
                value: 'replies',
                order: 'asc'
            },
            {
                label: t('sort.by_most_exited'),
                label_selected: t('sort.stories.most_exited_selected'),
                value: 'exits',
                order: 'desc'
            },
            {
                label: t('sort.by_least_exited'),
                label_selected: t('sort.stories.least_exited_selected'),
                value: 'exits',
                order: 'asc'
            },
            {
                label: t('sort.by_most_exit_rate'),
                label_selected: t('sort.stories.most_exit_rate_selected'),
                value: 'exits_rate',
                order: 'desc'
            },
            {
                label: t('sort.by_least_exit_rate'),
                label_selected: t('sort.stories.least_exit_rate_selected'),
                value: 'exits_rate',
                order: 'asc'
            },
            {
                label: t('sort.by_most_forward'),
                label_selected: t('sort.stories.most_forward_selected'),
                value: 'taps_forward',
                order: 'desc'
            },
            {
                label: t('sort.by_least_forward'),
                label_selected: t('sort.stories.least_forward_selected'),
                value: 'taps_forward',
                order: 'asc'
            },
            {
                label: t('sort.by_most_back'),
                label_selected: t('sort.stories.most_back_selected'),
                value: 'taps_back',
                order: 'desc'
            },
            {
                label: t('sort.by_least_back'),
                label_selected: t('sort.stories.least_back_selected'),
                value: 'taps_back',
                order: 'asc'
            }
        ] as SortOptionType[]
    };
};

export function getSortOptions(type: string, t: Translate) {
    switch (type) {
        case STORY:
            return storiesSortOptions(t);
        case POST:
            return postSortOptions(t);
        default:
            return postSortOptions(t);
    }
}
